import React, { FC, useMemo } from 'react';
import { Link } from 'react-router-dom';

import LoadingSpinner from '../../components/nav/LoadingSpinner';
import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';
import { FormulaType } from '../../constants/formulaType';
import routes from '../../constants/routes';

import useFormulasCategoryCountQuery from '../../hooks/formulas/useFormulasCategoryCount';
import usePushIntegrationQuery from '../../hooks/pushIntegrations/usePushIntegrationsQuery';

import { FormulaCategory, FormulaTypesData } from '../../models/formula.model';

const CHANNEL_ORDER: FormulaType[] = [
    FormulaType.mobilePush,
    FormulaType.email,
    FormulaType.sms,
    FormulaType.webPush,
    FormulaType.whatsapp,
];

const ChannelMixDistribution: FC<React.PropsWithChildren<unknown>> = () => {
    const { data: formulasCount, isLoading } = useFormulasCategoryCountQuery();
    const { data: integrationData, isLoading: isIntegrationDataLoading } =
        usePushIntegrationQuery();

    const channelCounts = useMemo<Record<FormulaType, number>>(() => {
        const defaults = Object.fromEntries(
            CHANNEL_ORDER.map((ch) => [ch, 0]),
        ) as Record<FormulaType, number>;

        if (!formulasCount) return defaults;

        const liveMessages = formulasCount[FormulaCategory.Live];
        return {
            ...defaults,
            ...Object.fromEntries(
                CHANNEL_ORDER.map((ch) => [ch, liveMessages[ch] ?? 0]),
            ),
        };
    }, [formulasCount]);

    const availableChannels = useMemo<Set<FormulaType>>(() => {
        if (!integrationData) return new Set<FormulaType>();
        return new Set(integrationData.flatMap((i) => i.channels ?? []));
    }, [integrationData]);

    const sortedChannels = useMemo<FormulaType[]>(() => {
        return [...CHANNEL_ORDER]
            .filter((ch) => availableChannels.has(ch))
            .sort((a, b) => channelCounts[b] - channelCounts[a]);
    }, [channelCounts, availableChannels]);

    if (isLoading || isIntegrationDataLoading) {
        return <LoadingSpinner styles={{ height: 100 }} />;
    }

    return (
        <Container>
            <Body>
                {sortedChannels.map((channel) => {
                    const count = channelCounts[channel];
                    const { title, Icon } = FormulaTypesData[channel];
                    return (
                        <ChannelRow key={channel}>
                            <ChannelLeft>
                                <Icon width={20} height={20} />
                                <ChannelName>{title}</ChannelName>
                            </ChannelLeft>
                            {count > 0 ? (
                                <LinkTo
                                    target="_blank"
                                    rel="noopener"
                                    to={`${routes.formulasCatalogWithTabs(
                                        FormulaCategory.Live,
                                    )}?message_type=${channel}`}
                                >
                                    <ChannelCount>{count}</ChannelCount>
                                </LinkTo>
                            ) : (
                                <LinkTo
                                    target="_blank"
                                    rel="noopener"
                                    to={`${routes.formulasCreate}?message_type=${channel}`}
                                >
                                    Create
                                </LinkTo>
                            )}
                        </ChannelRow>
                    );
                })}
            </Body>
        </Container>
    );
};

const Container = styled.div(() => ({
    padding: aampeTheme.spacing4(4),
    borderRadius: aampeTheme.borderRadius.medium,
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
}));

const Body = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(5),
    width: '100%',
    height: '100%',
}));

const ChannelRow = styled.div(() => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
}));

const ChannelLeft = styled.div(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
    flexShrink: 0,
}));

const ChannelName = styled.span(() => ({
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.medium,
    color: aampeTheme.colors.neutral09,
    whiteSpace: 'nowrap',
}));

const ChannelCount = styled.span(() => ({
    fontSize: aampeTheme.size.font.default,
    fontWeight: 700,
    color: aampeTheme.colors.solidA01,
    flexShrink: 0,
}));

const LinkTo = styled(Link)(() => ({
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.medium,
    color: aampeTheme.colors.solidA01,
    flexShrink: 0,
    whiteSpace: 'nowrap',
}));

export default ChannelMixDistribution;
